import React, { useRef, useState } from 'react'
import type { FileMetadataDto } from '../../Models/Workspace'
import styles from './WorkspaceFileAttachments.module.css'

type WorkspaceFileAttachmentsProps = {
  files: FileMetadataDto[]
  onUpload: (file: File) => void
  onDelete: (id: string, fileName: string) => void
  uploading?: boolean
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const allowedMimeTypes = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
])

const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx']

const isAllowedFile = (file: File) => {
  const mimeType = file.type.trim().toLowerCase()
  if (mimeType.startsWith('image/') || allowedMimeTypes.has(mimeType)) {
    return true
  }

  const fileName = file.name.toLowerCase()
  return allowedExtensions.some((extension) => fileName.endsWith(extension))
}

const WorkspaceFileAttachments = ({
  files,
  onUpload,
  onDelete,
  uploading = false,
}: WorkspaceFileAttachmentsProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [validationError, setValidationError] = useState<string | null>(null)

  const handleCandidateFiles = (candidateFiles: File[]) => {
    const invalidFiles = candidateFiles.filter((file) => !isAllowedFile(file))
    if (invalidFiles.length > 0) {
      setValidationError('Only PDF, DOC, DOCX, XLS, XLSX, and image files are allowed.')
    } else {
      setValidationError(null)
    }

    candidateFiles.filter(isAllowedFile).forEach((file) => onUpload(file))
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length === 0) return
    handleCandidateFiles(droppedFiles)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>Attachments</span>
        <button
          type="button"
          className={styles.uploadBtn}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : '+ Add file'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
          className={styles.hiddenInput}
          onChange={(e) => {
            const selectedFiles = Array.from(e.target.files ?? [])
            if (selectedFiles.length === 0) return

            handleCandidateFiles(selectedFiles)
            e.target.value = ''
          }}
        />
      </div>
      {validationError ? <p className={styles.validationError}>{validationError}</p> : null}

      {files.length === 0 ? (
        <div
          className={styles.dropZone}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 16V8m0 0l-3 3m3-3l3 3M5 20h14"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Drop a file here or click Add file</span>
        </div>
      ) : (
        <ul className={styles.fileList}>
          {files.map((file) => (
            <li key={file.id} className={styles.fileItem}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className={styles.fileIcon}>
                <path
                  d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinejoin="round"
                />
                <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
              </svg>
              <div className={styles.fileMeta}>
                <a
                  href={file.blobUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.fileName}
                  title={file.fileName}
                >
                  {file.fileName}
                </a>
                <span className={styles.fileSize}>{formatBytes(file.sizeBytes)}</span>
              </div>
              <button
                type="button"
                className={styles.deleteBtn}
                title="Remove attachment"
                onClick={() => {
                  if (window.confirm(`Remove "${file.fileName}"?`)) {
                    onDelete(file.id, file.fileName)
                  }
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default WorkspaceFileAttachments
